/**
 * Шторка с тремя положениями и перетаскиванием пальцем.
 *
 * Позиция во время жеста пишется прямо в style.transform, минуя React:
 * ре-рендер на каждое движение пальца — верный способ получить рывки.
 * В состояние уходит только результат — снап и видимая высота.
 */

import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type RefObject,
} from 'react';

export type SnapId = 'collapsed' | 'mid' | 'full';

const SNAP_ORDER: SnapId[] = ['collapsed', 'mid', 'full'];

/** За пределами крайних положений тянется вязко, а не свободно. */
const RUBBER = 0.32;
/** Насколько далеко «доедет» шторка по инерции: скорость (px/мс) × это время. */
const FLING_MS = 140;

interface Metrics {
  /** Отступ сверху в полностью раскрытом состоянии. */
  fullTop: number;
  midTop: number;
  collapsedTop: number;
  viewportH: number;
}

export interface BottomSheetApi {
  snap: SnapId;
  snapTo(next: SnapId): void;
  /** Циклическое переключение по тапу на ручку. */
  cycle(): void;
  /** Сколько шторки видно сейчас, px — карте нужно для отступа снизу. */
  visibleHeight: number;
  /** Верхняя граница полностью раскрытой шторки, px от верха экрана. */
  fullTop: number;
  sheetRef: RefObject<HTMLDivElement>;
  /** Замеряем «шапку» шторки — она задаёт свёрнутую высоту. */
  peekRef: RefObject<HTMLDivElement>;
  dragging: boolean;
  onDragStart(event: ReactPointerEvent<HTMLElement>): void;
}

function computeMetrics(peekHeight: number): Metrics {
  const viewportH = window.visualViewport?.height ?? window.innerHeight;
  // Сверху всегда остаётся полоска карты — иначе теряется контекст «где я».
  const fullTop = Math.max(72, Math.round(viewportH * 0.12));
  const midTop = Math.round(viewportH * 0.52);
  const collapsedTop = Math.max(
    midTop + 24,
    Math.round(viewportH - Math.max(96, peekHeight)),
  );
  return { fullTop, midTop, collapsedTop, viewportH };
}

export function useBottomSheet(initial: SnapId = 'mid'): BottomSheetApi {
  const sheetRef = useRef<HTMLDivElement>(null);
  const peekRef = useRef<HTMLDivElement>(null);

  const [snap, setSnap] = useState<SnapId>(initial);
  const [dragging, setDragging] = useState(false);
  const [metrics, setMetrics] = useState<Metrics>(() => computeMetrics(140));

  const offsets = useRef<Record<SnapId, number>>({
    full: 0,
    mid: 0,
    collapsed: 0,
  });
  offsets.current = {
    full: 0,
    mid: metrics.midTop - metrics.fullTop,
    collapsed: metrics.collapsedTop - metrics.fullTop,
  };

  const drag = useRef<{
    pointerId: number;
    startY: number;
    startOffset: number;
    lastY: number;
    lastT: number;
    velocity: number;
  } | null>(null);

  /** Пишем позицию в DOM. animate=false — во время жеста, палец не ждёт анимацию. */
  const applyOffset = useCallback((px: number, animate: boolean) => {
    const node = sheetRef.current;
    if (!node) return;
    node.style.transition = animate
      ? 'transform var(--dur-slow) var(--ease)'
      : 'none';
    node.style.transform = `translate3d(0, ${px}px, 0)`;
  }, []);

  // Пересчёт при смене размера окна и высоты «шапки».
  useLayoutEffect(() => {
    const update = (): void => {
      setMetrics(computeMetrics(peekRef.current?.offsetHeight ?? 140));
    };
    update();

    const observer = new ResizeObserver(update);
    if (peekRef.current) observer.observe(peekRef.current);
    window.addEventListener('resize', update);
    window.visualViewport?.addEventListener('resize', update);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', update);
      window.visualViewport?.removeEventListener('resize', update);
    };
  }, []);

  // Синхронизация DOM с текущим снапом (после отпускания, смены снапа, ресайза).
  useLayoutEffect(() => {
    if (drag.current) return;
    applyOffset(offsets.current[snap], true);
    const node = sheetRef.current;
    // Компенсация: часть скроллера уезжает за нижнюю кромку экрана.
    node?.style.setProperty('--sheet-gap', `${offsets.current[snap]}px`);
  }, [snap, metrics, applyOffset]);

  const snapTo = useCallback((next: SnapId) => setSnap(next), []);

  const cycle = useCallback(() => {
    setSnap((current) => {
      const index = SNAP_ORDER.indexOf(current);
      return SNAP_ORDER[(index + 1) % SNAP_ORDER.length] ?? 'mid';
    });
  }, []);

  const onDragStart = useCallback(
    (event: ReactPointerEvent<HTMLElement>) => {
      if (drag.current) return;
      if (event.pointerType === 'mouse' && event.button !== 0) return;

      const target = event.currentTarget;
      target.setPointerCapture(event.pointerId);
      drag.current = {
        pointerId: event.pointerId,
        startY: event.clientY,
        startOffset: offsets.current[snap],
        lastY: event.clientY,
        lastT: event.timeStamp,
        velocity: 0,
      };
      setDragging(true);
    },
    [snap],
  );

  // Движение и отпускание слушаем на окне: палец легко уходит за пределы ручки.
  useEffect(() => {
    if (!dragging) return;

    const onMove = (event: PointerEvent): void => {
      const state = drag.current;
      if (!state || event.pointerId !== state.pointerId) return;

      const min = 0;
      const max = offsets.current.collapsed;
      const raw = state.startOffset + (event.clientY - state.startY);
      const bounded =
        raw < min
          ? min + (raw - min) * RUBBER
          : raw > max
            ? max + (raw - max) * RUBBER
            : raw;

      const dt = event.timeStamp - state.lastT;
      if (dt > 0) {
        const instant = (event.clientY - state.lastY) / dt;
        // Сглаживание: одиночный дёрганый сэмпл не должен решать за жест.
        state.velocity = state.velocity * 0.6 + instant * 0.4;
        state.lastY = event.clientY;
        state.lastT = event.timeStamp;
      }

      applyOffset(bounded, false);
    };

    const finish = (event: PointerEvent): void => {
      const state = drag.current;
      if (!state || event.pointerId !== state.pointerId) return;

      const current = state.startOffset + (event.clientY - state.startY);
      const predicted = current + state.velocity * FLING_MS;

      let best: SnapId = snap;
      let bestDistance = Number.POSITIVE_INFINITY;
      for (const id of SNAP_ORDER) {
        const distance = Math.abs(offsets.current[id] - predicted);
        if (distance < bestDistance) {
          bestDistance = distance;
          best = id;
        }
      }

      drag.current = null;
      setDragging(false);
      if (best === snap) {
        applyOffset(offsets.current[snap], true);
        sheetRef.current?.style.setProperty(
          '--sheet-gap',
          `${offsets.current[snap]}px`,
        );
      } else {
        setSnap(best);
      }
    };

    window.addEventListener('pointermove', onMove, { passive: true });
    window.addEventListener('pointerup', finish);
    window.addEventListener('pointercancel', finish);
    return () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', finish);
      window.removeEventListener('pointercancel', finish);
    };
  }, [dragging, snap, applyOffset]);

  const visibleHeight = metrics.viewportH - (metrics.fullTop + offsets.current[snap]);

  return {
    snap,
    snapTo,
    cycle,
    visibleHeight,
    fullTop: metrics.fullTop,
    sheetRef,
    peekRef,
    dragging,
    onDragStart,
  };
}
